import { ReactComponentElement, Ref, RefObject } from "react";
import { IonContent, IonPage } from "@ionic/react";
import {
  AliasBanner,
  BannerContainer,
  Breakpoints,
  Colors,
  LoadingInterstitial,
  useFeatureFlags,
} from "swing-components";

import {
  ContentDualColumn,
  ContentSingleColumn,
  DesktopNavigation,
  DesktopNavigationProps,
  ExpiringDocumentsBanner,
  MobileNavigation,
  MobileNavigationProps,
} from "~components";
import { ExpiringDocumentsDetail, translate as t } from "~utils";

export type PageProps = {
  /**
   * When true, show a loading animation and hide the children content.
   * The navigation will still be showing.
   * @default false
   */
  isLoading?: boolean;
  /**
   * Props for the desktop navigation.
   */
  desktopNavigationProps: DesktopNavigationProps;
  /**
   * Props for the mobile navigation
   */
  mobileNavigationProps: MobileNavigationProps;
  /**
   * Ref for scroll to top of page
   */
  reference: Ref<HTMLIonContentElement>;
  /**
   * Children to render when `isLoading` is false.
   */
  children?: ReactComponentElement<typeof ContentSingleColumn | typeof ContentDualColumn>;
  /**
   * When true, add alias banner and styling
   */
  isAliased?: boolean;
  /**
   * Email for the alias banner, only used when isAliased is true
   * */
  aliasEmail?: string;
  /**
   * Data to render in the page
   */
  data?: { expiringDocumentsData?: ExpiringDocumentsDetail };
};

/**
 * Responsible for rendering the navigation with the content following the
 * appropriate layout.
 */
export function Page(props: PageProps) {
  const {
    isLoading,
    desktopNavigationProps,
    mobileNavigationProps,
    reference,
    children,
    isAliased = false,
    aliasEmail,
    data,
  } = props;

  const { flags } = useFeatureFlags();
  const { expiringDocumentsData = { status: "DOC_VALID" } } = data || {};
  const showExpiringBanner =
    flags.expiringDocumentBannerV2 && expiringDocumentsData?.status !== "DOC_VALID";

  return (
    <IonPage
      css={{
        display: "flex",
        backgroundColor: Colors.white300,
        flexDirection: "column",
        [Breakpoints.mediumDeviceAndUp]: {
          flexDirection: "row",
        },
      }}
    >
      {/* Navigations */}
      <div
        css={{
          display: "none",
          position: "sticky",
          inset: "0 0 auto 0",
          zIndex: 2,
          [Breakpoints.mediumDeviceAndUp]: {
            display: "block",
          },
        }}
      >
        <DesktopNavigation {...desktopNavigationProps} />
      </div>
      <div
        css={{
          display: "block",
          // This moves the mobile nav bar to the bottom of the flex area
          order: 2,
          position: "sticky",
          bottom: 0,
          zIndex: 2,
          [Breakpoints.mediumDeviceAndUp]: {
            display: "none",
          },
        }}
      >
        <MobileNavigation {...mobileNavigationProps} />
      </div>

      {/* Content */}
      <IonContent css={{ flexGrow: 1, display: "flex" }} ref={reference} id="ion-content">
        {isLoading ? (
          <div
            css={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <LoadingInterstitial />
          </div>
        ) : (
          <>
            {(isAliased || showExpiringBanner) && (
              <BannerContainer>
                {isAliased && <AliasBanner email={aliasEmail || t("MISSING_EMAIL")} />}
                {showExpiringBanner && <ExpiringDocumentsBanner {...expiringDocumentsData} />}
              </BannerContainer>
            )}
            {children}
          </>
        )}
      </IonContent>
    </IonPage>
  );
}

export const goToTop = async (ref: RefObject<HTMLIonContentElement>) => {
  if (ref.current) {
    await ref.current.scrollToTop(500);
  }
};
